import { ResponseHandler } from './responseHandlers';
import { BusinessPlanSection, SectionId, SectionIdEnum, SECTION_METADATA, GenerationContext } from '../../types/businessPlan';
import type { APIResponse } from './types';

export class AnthropicService {
  private baseUrl: string;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl || '';
  }

  // Make a request to the API
  async makeRequest({ endpoint, data }: { endpoint: string; data: any }): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API request failed: ${response.status} ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  // Generate a complete business plan
  async generateCompletePlan(context: GenerationContext): Promise<Record<SectionId, BusinessPlanSection>> {
    try {
      console.log('Generating complete plan with context:', context);
      
      // Ensure we have all the required data
      if (!context.businessIdea) {
        throw new Error('Business idea is required for plan generation');
      }
      
      // Create a detailed prompt that includes all the additional data
      const detailedPrompt = this.createDetailedPrompt(context);
      
      const response = await this.makeRequest({
        endpoint: '/api/business-plans/generate-plan',
        data: {
          context: {
            businessIdea: context.businessIdea,
            additionalData: context.additionalData,
            detailedPrompt // Include the detailed prompt
          }
        }
      });
      
      console.log('Response from generate-plan API:', response);
      
      // Initialize with empty sections
      const processedSections = ResponseHandler.createEmptySections();
      
      // If the response has sections property, use it directly
      if (response && response.sections && typeof response.sections === 'object') {
        console.log('Found sections in response, processing...');
        
        // Process each section
        Object.entries(response.sections).forEach(([key, value]: [string, any]) => {
          if (Object.values(SectionIdEnum).includes(key as any)) {
            const sectionId = key as SectionId;
            const metadata = SECTION_METADATA[sectionId];
            
            // Extract content
            let content = '';
            let status: 'not-started' | 'generating' | 'completed' | 'error' = 'not-started';
            let subsections: BusinessPlanSection['subsections'] = [];
            
            if (typeof value === 'string') {
              content = value;
              status = 'completed';
              // Generate subsections from content
              subsections = this.generateSubsectionsFromContent(content, sectionId);
            } else if (value && typeof value === 'object') {
              content = value.content || '';
              status = value.status || (content ? 'completed' : 'not-started');
              
              // If subsections are provided, use them
              if (value.subsections && Array.isArray(value.subsections)) {
                subsections = value.subsections;
              } else if (content) {
                // Generate subsections from content
                subsections = this.generateSubsectionsFromContent(content, sectionId);
              }
            }
            
            // Update the section
            if (sectionId in processedSections) {
              processedSections[sectionId] = {
                ...processedSections[sectionId],
                content: content || '',
                status: content ? 'completed' : 'not-started',
                subsections: subsections
              };
            }
          }
        });
        
        // Generate table of contents that includes subsections
        processedSections['table-of-contents'] = this.generateTableOfContents(processedSections);
        
        return processedSections;
      }
      
      // If the response has content property and it's a string, parse it
      if (response.content && typeof response.content === 'string') {
        console.log('Parsing content to sections...');
        const parsedSections = ResponseHandler.parseContentToSections(response.content);
        
        // Generate subsections for each section
        Object.keys(parsedSections).forEach(key => {
          const sectionId = key as SectionId;
          const section = parsedSections[sectionId];
          
          if (section.content) {
            section.subsections = this.generateSubsectionsFromContent(section.content, sectionId);
          }
        });
        
        // Generate table of contents
        parsedSections['table-of-contents'] = this.generateTableOfContents(parsedSections);
        
        return parsedSections;
      }
      
      // If we have a raw text response, try to parse it
      if (typeof response === 'string') {
        console.log('Response is a string, parsing...');
        const parsedSections = ResponseHandler.parseContentToSections(response);
        
        // Generate subsections for each section
        Object.keys(parsedSections).forEach(key => {
          const sectionId = key as SectionId;
          const section = parsedSections[sectionId];
          
          if (section.content) {
            section.subsections = this.generateSubsectionsFromContent(section.content, sectionId);
          }
        });
        
        // Generate table of contents
        parsedSections['table-of-contents'] = this.generateTableOfContents(parsedSections);
        
        return parsedSections;
      }
      
      // If we can't find any content, create empty sections
      console.log('No content found in response, creating empty sections');
      
      // Generate some basic content based on the business idea and additional data
      return this.generateBasicSections(context);
    } catch (error) {
      console.error('Error generating complete plan:', error);
      throw error;
    }
  }

  // Helper method to create a detailed prompt that includes all additional data
  private createDetailedPrompt(context: GenerationContext): string {
    const { businessIdea, additionalData } = context;
    
    let prompt = `Generate a comprehensive business plan for the following business idea: ${businessIdea.title}\n\n`;
    prompt += `Description: ${businessIdea.description}\n\n`;
    
    // Add detailed description if available (only from CustomBusinessPlanWizard)
    if (businessIdea.detailedDescription) {
      prompt += `Detailed Explanation:\n${businessIdea.detailedDescription}\n\n`;
    }
    
    // Add additional data from the wizard if available
    if (additionalData) {
      prompt += 'Additional Information:\n';
      
      if (additionalData.businessName) {
        prompt += `Business Name: ${additionalData.businessName}\n`;
      }
      
      if (additionalData.founderName) {
        prompt += `Founder Name: ${additionalData.founderName}\n`;
      }
      
      if (additionalData.startDate) {
        prompt += `Start Date: ${additionalData.startDate}\n`;
      }
      
      // Determine currency based on location
      let currency = 'USD';
      let currencySymbol = '$';
      
      if (additionalData.location && additionalData.location.country) {
        prompt += `Location: ${additionalData.location.city || ''}, ${additionalData.location.state || ''} ${additionalData.location.country}\n`;
        
        // Set currency based on country
        switch(additionalData.location.country.toLowerCase()) {
          case 'uk':
          case 'united kingdom':
          case 'great britain':
          case 'england':
          case 'scotland':
          case 'wales':
          case 'northern ireland':
            currency = 'GBP';
            currencySymbol = '£';
            break;
          case 'eu':
          case 'europe':
          case 'european union':
          case 'france':
          case 'germany':
          case 'italy':
          case 'spain':
          case 'netherlands':
          case 'belgium':
          case 'portugal':
          case 'ireland':
            currency = 'EUR';
            currencySymbol = '€';
            break;
          case 'canada':
            currency = 'CAD';
            currencySymbol = 'C$';
            break;
          case 'australia':
            currency = 'AUD';
            currencySymbol = 'A$';
            break;
          case 'japan':
            currency = 'JPY';
            currencySymbol = '¥';
            break;
          default:
            currency = 'USD';
            currencySymbol = '$';
        }
        
        // Add explicit instruction about currency
        prompt += `Currency: Use ${currency} (${currencySymbol}) for all financial figures and calculations.\n`;
      } else if (additionalData.location) {
        prompt += `Location: ${additionalData.location.city || ''}, ${additionalData.location.state || ''}\n`;
      }
      
      if (additionalData.targetMarket) {
        prompt += `Target Market: ${additionalData.targetMarket}\n`;
      }
      
      if (additionalData.funding) {
        prompt += `Funding: ${currencySymbol}${additionalData.funding.amount} (Source: ${additionalData.funding.source})\n`;
        prompt += `IMPORTANT: Use this exact investment amount (${currencySymbol}${additionalData.funding.amount}) in all financial projections and calculations.\n`;
      }
    }
    
    prompt += '\nPlease include the following sections in the business plan:\n';
    prompt += '1. Executive Summary\n';
    prompt += '2. Business Overview\n';
    prompt += '3. Market Analysis\n';
    prompt += '4. SWOT Analysis\n';
    prompt += '5. Business Models\n';
    prompt += '6. Viability Analysis\n';
    prompt += '7. Products & Services\n';
    prompt += '8. Sales & Marketing Strategy\n';
    prompt += '9. Operations Plan\n';
    prompt += '10. Financial Projections\n';
    prompt += '11. Implementation Plan\n';
    
    prompt += '\nFor each section, please provide detailed subsections with specific information relevant to the business idea.';
    prompt += '\nIn the Financial Projections section, include realistic financial figures based on the provided investment amount and use the appropriate currency format.';
    
    return prompt;
  }
  
  // Helper method to generate subsections from content
  private generateSubsectionsFromContent(content: string, sectionId: SectionId): BusinessPlanSection['subsections'] {
    if (!content) return [];
    
    try {
      // Split content by potential subsection headers
      const lines = content.split('\n');
      const subsections: BusinessPlanSection['subsections'] = [];
      let currentTitle = '';
      let currentContent: string[] = [];
      let subsectionIndex = 0;
      
      // Common subsection header patterns
      const headerPatterns = [
        /^\d+\.\s+([\w\s&]+)/i,  // Numbered headers: "1. Title"
        /^[A-Z][^.!?:]*:/,       // Title with colon: "Title:"
        /^#+\s+([\w\s&]+)/i,    // Markdown headers: "# Title"
        /^[A-Z][\w\s&]+(\s+)?$/  // All caps titles: "TITLE"
      ];
      
      // Process each line to identify subsection headers and content
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Skip empty lines
        if (!line) continue;
        
        // Check if this line looks like a subsection header
        const isSubsectionHeader = headerPatterns.some(pattern => pattern.test(line));
        
        if (isSubsectionHeader) {
          // If we were already collecting content for a subsection, save it
          if (currentTitle && currentContent.length > 0) {
            const id = `${sectionId}-subsection-${subsectionIndex}`;
            subsections.push({
              id,
              title: currentTitle,
              content: currentContent.join('\n'),
              status: 'completed',
              pageNumber: SECTION_METADATA[sectionId].pageNumber + subsectionIndex
            });
            subsectionIndex++;
          }
          
          // Start a new subsection
          currentTitle = line;
          currentContent = [];
        } else if (currentTitle) {
          // Add this line to the current subsection's content
          currentContent.push(line);
        } else {
          // If we haven't found a title yet, this might be the start of the content
          // Use a default title for the first subsection
          currentTitle = `${SECTION_METADATA[sectionId].title} Overview`;
          currentContent.push(line);
        }
      }
      
      // Save the last subsection if there is one
      if (currentTitle && currentContent.length > 0) {
        const id = `${sectionId}-subsection-${subsectionIndex}`;
        subsections.push({
          id,
          title: currentTitle,
          content: currentContent.join('\n'),
          status: 'completed',
          pageNumber: SECTION_METADATA[sectionId].pageNumber + subsectionIndex
        });
      }
      
      // If no subsections were found, create at least one with the entire content
      if (subsections.length === 0 && content.trim()) {
        const id = `${sectionId}-subsection-0`;
        subsections.push({
          id,
          title: `${SECTION_METADATA[sectionId].title} Overview`,
          content: content,
          status: 'completed',
          pageNumber: SECTION_METADATA[sectionId].pageNumber
        });
      }
      
      return subsections;
    } catch (error) {
      console.error(`Error generating subsections for ${sectionId}:`, error);
      return [];
    }
  }
  
  // Generate a table of contents that includes subsections
  private generateTableOfContents(sections: Record<SectionId, BusinessPlanSection>): BusinessPlanSection {
    const metadata = SECTION_METADATA['table-of-contents'];
    let content = '# Table of Contents\n\n';
    
    // Add each section to the table of contents
    Object.values(sections)
      .sort((a, b) => a.pageNumber - b.pageNumber)
      .forEach(section => {
        // Skip the table of contents itself
        if (section.id === 'table-of-contents') return;
        
        content += `${section.pageNumber}. ${section.title}\n`;
        
        // Add subsections if they exist
        if (section.subsections && section.subsections.length > 0) {
          section.subsections.forEach(subsection => {
            content += `   ${section.pageNumber}.${subsection.pageNumber - section.pageNumber}. ${subsection.title}\n`;
          });
        }
        
        content += '\n';
      });
    
    return {
      id: 'table-of-contents',
      title: metadata.title,
      content,
      status: 'completed',
      pageNumber: metadata.pageNumber
    };
  }
  
  // Generate basic sections with content based on the business idea and additional data
  private generateBasicSections(context: GenerationContext): Record<SectionId, BusinessPlanSection> {
    const { businessIdea, additionalData } = context;
    const sections = ResponseHandler.createEmptySections();
    
    // Generate basic content for each section
    if (businessIdea) {
      // Cover Page
      sections['cover-page'] = {
        ...sections['cover-page'],
        content: `# ${additionalData?.businessName || businessIdea.title}\n\nBusiness Plan\n\nPrepared by: ${additionalData?.founderName || 'Business Owner'}\n\nDate: ${new Date().toLocaleDateString()}`,
        status: 'completed'
      };
      
      // Executive Summary
      sections['executive-summary'] = {
        ...sections['executive-summary'],
        content: `# Executive Summary\n\n${businessIdea.description}\n\nThis business plan outlines the strategy for ${additionalData?.businessName || businessIdea.title}, a business focused on ${businessIdea.title.toLowerCase()}.`,
        status: 'completed',
        subsections: [{
          id: 'executive-summary-subsection-0',
          title: 'Business Overview',
          content: businessIdea.description,
          status: 'completed',
          pageNumber: sections['executive-summary'].pageNumber
        }]
      };
      
      // Business Overview
      sections['overview'] = {
        ...sections['overview'],
        content: `# Business Overview\n\n${businessIdea.description}\n\n${additionalData?.businessName || businessIdea.title} will be located in ${additionalData?.location?.city || 'the target market'} and will serve ${additionalData?.targetMarket || 'the identified target market'}.`,
        status: 'completed',
        subsections: [{
          id: 'overview-subsection-0',
          title: 'Mission Statement',
          content: `To provide exceptional ${businessIdea.title.toLowerCase()} services to ${additionalData?.targetMarket || 'our customers'}.`,
          status: 'completed',
          pageNumber: sections['overview'].pageNumber
        }]
      };
    }
    
    // Generate table of contents
    sections['table-of-contents'] = this.generateTableOfContents(sections);
    
    return sections;
  }

  // Generate subsections for a specific section
  async generateSubsections(sectionId: SectionId, context: GenerationContext): Promise<BusinessPlanSection['subsections']> {
    try {
      const response = await this.makeRequest({
        endpoint: '/api/business-plans/generate-subsections',
        data: { sectionId, context }
      });
      
      // Extract subsections from the response content
      return ResponseHandler.extractSubsections(response.content, sectionId);
    } catch (error) {
      console.error(`Error generating subsections for ${sectionId}:`, error);
      // Return empty array instead of throwing to prevent breaking the main plan
      return [];
    }
  }

  // Regenerate a specific section
  async regenerateSection(sectionId: SectionId, context: GenerationContext): Promise<BusinessPlanSection> {
    try {
      const response = await this.makeRequest({
        endpoint: '/api/business-plans/regenerate-section',
        data: { sectionId, context }
      });
      return ResponseHandler.formatSection(response, sectionId);
    } catch (error) {
      console.error('Error regenerating section:', error);
      throw error;
    }
  }
}
